// src/components/Tabs/EntityTrafficTab.jsx
import React from 'react';
import EntityShareChart from '../Charts/EntityShareChart';

const EntityTrafficTab = ({ showDetails, openDrillDown }) => {
  const entities = [
    { 
      id: 'maersk', 
      name: 'Maersk Line', 
      shortName: 'M', 
      color: '#ff6384', 
      marketShare: '28.5%', 
      volume: '52,840 T', 
      growth: '+3.2%',
      borderColor: 'maersk'
    },
    { 
      id: 'msc', 
      name: 'Mediterranean Shipping', 
      shortName: 'MSC', 
      color: '#ff9f40', 
      marketShare: '22.3%', 
      volume: '41,350 T', 
      growth: '+5.1%',
      borderColor: 'msc'
    },
    { 
      id: 'cma', 
      name: 'CMA CGM Group', 
      shortName: 'CMA', 
      color: '#4cd964', 
      marketShare: '18.7%', 
      volume: '34,680 T', 
      growth: '+2.8%',
      borderColor: 'cma'
    },
    { 
      id: 'cosco', 
      name: 'COSCO Shipping', 
      shortName: 'C', 
      color: '#9966ff', 
      marketShare: '15.2%', 
      volume: '28,180 T', 
      growth: '+7.5%',
      borderColor: 'cosco'
    }
  ];

  // Handle entity card click
  const handleEntityClick = (entity) => {
    if (openDrillDown) {
      const entityData = {
        'maersk': {
          title: 'Maersk Line Analysis',
          icon: 'fas fa-ship',
          iconColor: '#ff6384',
          category: 'Shipping Entity',
          summary: 'Maersk Line leads with 28.5% market share, handling 52,840 tons monthly.',
          details: 'As the market leader, Maersk maintains strong operational efficiency and customer relationships.',
          keyMetrics: [
            { label: 'Market Share', value: '28.5%', change: '+0.8%' },
            { label: 'Monthly Volume', value: '52,840 T', change: '+3.2%' },
            { label: 'Vessels Deployed', value: '42', change: '+2 vessels' },
            { label: 'Customer Rating', value: '4.8/5', change: 'Excellent' }
          ],
          companyOverview: {
            'Founded': '1904',
            'Headquarters': 'Copenhagen, Denmark',
            'Parent Company': 'A.P. Moller-Maersk',
            'Global Ranking': '2nd largest container shipping',
            'Fleet Size': '708 vessels',
            'Total Capacity': '4.1M TEU'
          },
          performanceMetrics: {
            'Avg Turnaround Time': '18.5 hours',
            'On-time Performance': '96.2%',
            'Crane Productivity': '30.2 moves/hour',
            'Vessel Utilization': '92.5%',
            'Safety Index': '99.1%'
          },
          serviceNetwork: [
            'Asia-Europe (AE1, AE7, AE10 services)',
            'Trans-Pacific (TP2, TP6, TP9 services)',
            'Asia-Middle East (ME2 service)',
            'Europe-South America (SA2 service)',
            'Intra-Asia network'
          ],
          vesselTypes: [
            { type: 'Container Ships', count: '380', capacity: '3.2M TEU' },
            { type: 'Multi-purpose', count: '45', capacity: 'Various' },
            { type: 'Reefer Vessels', count: '25', capacity: 'Specialized' },
            { type: 'Others', count: '258', capacity: 'Diverse' }
          ],
          growthStrategy: [
            'Focus on digital transformation',
            'Expansion in e-commerce logistics',
            'Sustainability initiatives',
            'Cold chain development'
          ],
          sustainabilityInitiatives: [
            'Net zero by 2040 target',
            'Methanol-powered vessel orders',
            'Carbon-neutral logistics options',
            'Digital optimization for efficiency'
          ],
          recentDevelopments: [
            'Ordered 12 methanol-powered vessels',
            'Expanded e-commerce logistics network',
            'Launched new Asia-Europe service',
            'Digital booking platform enhancements'
          ],
          challenges: [
            'High operating costs',
            'Intense market competition',
            'Environmental regulations',
            'Supply chain disruptions'
          ],
          competitiveAdvantages: [
            'Global network coverage',
            'Digital platform maturity',
            'Strong brand reputation',
            'Operational efficiency'
          ],
          forecast: 'Expected to maintain 28-29% market share through 2024',
          confidence: 'High (strong market position)'
        },
        'msc': {
          title: 'Mediterranean Shipping Company Analysis',
          icon: 'fas fa-ship',
          iconColor: '#ff9f40',
          category: 'Shipping Entity',
          summary: 'MSC holds 22.3% market share with strong growth of 5.1% quarterly.',
          details: 'Rapidly expanding through acquisitions and newbuilding programs.',
          keyMetrics: [
            { label: 'Market Share', value: '22.3%', change: '+1.2%' },
            { label: 'Monthly Volume', value: '41,350 T', change: '+5.1%' },
            { label: 'Vessels Deployed', value: '38', change: '+3 vessels' },
            { label: 'Customer Rating', value: '4.6/5', change: 'Very Good' }
          ],
          companyOverview: {
            'Founded': '1970',
            'Headquarters': 'Geneva, Switzerland',
            'Parent Company': 'MSC Group',
            'Global Ranking': 'Largest container shipping',
            'Fleet Size': '730 vessels',
            'Total Capacity': '4.8M TEU'
          }
        }
      };
      
      openDrillDown('entity', entityData[entity.id] || {
        title: `${entity.name} Analysis`,
        icon: 'fas fa-ship',
        iconColor: entity.color,
        category: 'Shipping Entity',
        summary: `${entity.name} holds ${entity.marketShare} market share with ${entity.growth} quarterly growth.`,
        details: `Detailed analysis of ${entity.name} performance and market position.`,
        keyMetrics: [
          { label: 'Market Share', value: entity.marketShare, change: entity.growth },
          { label: 'Monthly Volume', value: entity.volume, change: 'Current' },
          { label: 'Growth Rate', value: entity.growth, change: 'Quarterly' }
        ]
      }, `${entity.name} Analysis`);
    } else if (showDetails) {
      showDetails(`${entity.id}-details`);
    }
  };

  // Handle performance row click
  const handlePerformanceClick = (entityId) => {
    if (openDrillDown) {
      const performanceData = {
        'maersk-performance': {
          title: 'Maersk Line Performance Metrics',
          icon: 'fas fa-chart-line',
          iconColor: '#ff6384',
          category: 'Performance Analysis',
          summary: 'Maersk demonstrates excellent operational metrics with 96.2% on-time performance.',
          details: 'Comprehensive performance analysis showing leadership in key operational indicators.',
          performanceMetrics: [
            { metric: 'Vessels Deployed', value: '42', benchmark: 'Industry: 28', status: 'Excellent' },
            { metric: 'Avg Turnaround', value: '18.5h', benchmark: 'Industry: 20.8h', status: 'Excellent' },
            { metric: 'On-time Performance', value: '96.2%', benchmark: 'Industry: 91.5%', status: 'Excellent' },
            { metric: 'Customer Rating', value: '4.8/5', benchmark: 'Industry: 4.3/5', status: 'Excellent' },
            { metric: 'Crane Productivity', value: '30.2 moves/hr', benchmark: 'Industry: 26.5', status: 'Excellent' },
            { metric: 'Safety Index', value: '99.1%', benchmark: 'Industry: 97.8%', status: 'Excellent' }
          ],
          efficiencyAnalysis: {
            'Turnaround Time Advantage': '2.3 hours faster than industry',
            'Vessel Utilization': '92.5% (optimal)',
            'Crane Efficiency': '+13.9% above industry',
            'Cost per TEU': '$320 (competitive)'
          },
          serviceQuality: [
            'Digital booking: 98% satisfaction',
            'Documentation accuracy: 99.5%',
            'Cargo tracking: Real-time 24/7',
            'Customer support: 4.9/5 rating'
          ],
          technologyAdoption: [
            'AI-based scheduling system',
            'Blockchain documentation',
            'IoT container tracking',
            'Predictive maintenance'
          ],
          areasForImprovement: [
            'Reduce carbon footprint further',
            'Expand cold chain capabilities',
            'Enhance last-mile integration',
            'Improve port community system'
          ],
          comparativeAnalysis: [
            'Turnaround time: Best in class',
            'Customer satisfaction: Industry leader',
            'Technology adoption: Advanced',
            'Sustainability: Progressing well'
          ],
          strategicRecommendations: [
            'Maintain technology leadership',
            'Expand sustainable shipping options',
            'Enhance digital customer experience',
            'Optimize network efficiency'
          ],
          forecast: 'Expected to maintain operational leadership through 2024',
          confidence: 'High (consistent performance history)'
        },
        'msc-performance': {
          title: 'MSC Performance Metrics',
          icon: 'fas fa-chart-line',
          iconColor: '#ff9f40',
          category: 'Performance Analysis',
          summary: 'MSC shows strong operational metrics with rapid growth and expansion.',
          details: 'Performance analysis indicates competitive positioning and growth momentum.',
          performanceMetrics: [
            { metric: 'Vessels Deployed', value: '38', benchmark: 'Industry: 28', status: 'Excellent' },
            { metric: 'Avg Turnaround', value: '19.2h', benchmark: 'Industry: 20.8h', status: 'Very Good' },
            { metric: 'On-time Performance', value: '94.7%', benchmark: 'Industry: 91.5%', status: 'Very Good' },
            { metric: 'Customer Rating', value: '4.6/5', benchmark: 'Industry: 4.3/5', status: 'Very Good' }
          ]
        }
      };
      openDrillDown('performance', performanceData[entityId] || {
        title: `${entityId.split('-')[0].toUpperCase()} Performance`,
        icon: 'fas fa-chart-line',
        iconColor: '#4da6ff',
        category: 'Performance Analysis',
        summary: 'Performance metrics analysis for shipping entity.',
        details: 'Detailed operational performance indicators.',
        performanceMetrics: [
          { metric: 'Vessels Deployed', value: 'Data', benchmark: 'Industry', status: 'Good' },
          { metric: 'Avg Turnaround', value: 'Data', benchmark: 'Industry', status: 'Good' }
        ]
      }, `${entityId.split('-')[0].toUpperCase()} Performance`);
    }
  };

  return (
    <div id="entity-traffic-tab" className="tab-content">
      <div className="entity-traffic-grid">
        {entities.map(entity => (
          <div 
            key={entity.id}
            className={`entity-card ${entity.borderColor} clickable`}
            onClick={() => handleEntityClick(entity)}
          >
            <div className="entity-header">
              <div className="entity-logo" style={{backgroundColor: entity.color}}>
                {entity.shortName}
              </div>
              <div className="entity-name">{entity.name}</div>
            </div>
            <div className="entity-stats">
              <div className="stat-item">
                <div className="stat-value">{entity.marketShare}</div>
                <div className="stat-label">Market Share</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">{entity.volume}</div>
                <div className="stat-label">Monthly Volume</div>
              </div>
            </div>
            <div className="progress-bar">
              <div className="progress-fill" style={{width: entity.marketShare, backgroundColor: entity.color}}></div>
            </div>
            <div className="card-change positive" style={{marginTop: '15px'}}>
              <i className="fas fa-arrow-up"></i>
              <span>{entity.growth} increase from last quarter</span>
            </div>
          </div>
        ))}
      </div>

      <div className="charts-section">
        <div className="chart-container">
          <h3 className="section-title">
            <i className="fas fa-chart-line"></i>
            <span>Entity Traffic Share Trend (Last 12 Months)</span>
          </h3>
          <div style={{height: '400px'}}>
            <EntityShareChart 
              onClick={() => openDrillDown && openDrillDown('share-trend', {
                title: 'Market Share Trend Analysis',
                icon: 'fas fa-chart-line',
                iconColor: '#4da6ff',
                category: 'Market Analysis',
                summary: 'Market share trends show Maersk maintaining leadership while COSCO shows strongest growth.',
                details: 'Twelve-month trend analysis reveals shifting competitive dynamics among major shipping lines.',
                trendData: [
                  { month: 'May 2023', maersk: '28.2%', msc: '21.8%', cma: '18.5%', cosco: '14.8%' },
                  { month: 'Current', maersk: '28.5%', msc: '22.3%', cma: '18.7%', cosco: '15.2%' }
                ],
                keyObservations: [
                  'Maersk maintained steady 28-29% share',
                  'MSC gained 0.5% market share',
                  'COSCO showed strongest growth at +0.4%',
                  'Market concentration remains high'
                ],
                competitiveDynamics: [
                  'Maersk: Defending leadership position',
                  'MSC: Aggressive expansion through acquisitions',
                  'CMA CGM: Stable market presence',
                  'COSCO: Rapid growth from base'
                ],
                marketConcentration: {
                  'Top 4 Market Share': '84.7%',
                  'Herfindahl Index': '2,150',
                  'Market Concentration': 'Moderate-High',
                  'New Entrants': 'Limited presence'
                },
                growthFactors: [
                  'COSCO: New service launches and competitive pricing',
                  'MSC: Fleet expansion and acquisitions',
                  'Maersk: Service reliability and digital advantages',
                  'CMA CGM: Strategic partnerships'
                ],
                strategicImplications: [
                  'High concentration provides pricing stability',
                  'Growth opportunities for smaller players limited',
                  'Customer benefits from service competition',
                  'Technology becoming key differentiator'
                ],
                forecast: 'Expected consolidation to continue with top 4 maintaining 85%+ share',
                confidence: 'High (established market structure)'
              }, 'Market Share Trends')}
            />
          </div>
        </div>

        <div className="table-container">
          <h3 className="section-title">
            <i className="fas fa-ship"></i>
            <span>Shipping Line Performance Metrics</span>
          </h3>
          <table className="data-table">
            <thead>
              <tr>
                <th>Shipping Line</th>
                <th>Vessels Deployed</th>
                <th>Avg Turnaround</th>
                <th>On-time Performance</th>
                <th>Customer Rating</th>
              </tr>
            </thead>
            <tbody>
              <tr 
                className="clickable-row"
                onClick={() => handlePerformanceClick('maersk-performance')}
              >
                <td><strong>Maersk Line</strong></td>
                <td>42</td>
                <td>18.5h</td>
                <td><span className="status-indicator status-high">96.2%</span></td>
                <td><span className="status-indicator status-high">4.8/5</span></td>
              </tr>
              <tr 
                className="clickable-row"
                onClick={() => handlePerformanceClick('msc-performance')}
              >
                <td><strong>Mediterranean Shipping</strong></td>
                <td>38</td>
                <td>19.2h</td>
                <td><span className="status-indicator status-high">94.7%</span></td>
                <td><span className="status-indicator status-high">4.6/5</span></td>
              </tr>
              <tr 
                className="clickable-row"
                onClick={() => handlePerformanceClick('cma-performance')}
              >
                <td><strong>CMA CGM Group</strong></td>
                <td>35</td>
                <td>19.8h</td>
                <td><span className="status-indicator status-medium">91.5%</span></td>
                <td><span className="status-indicator status-medium">4.3/5</span></td>
              </tr>
              <tr 
                className="clickable-row"
                onClick={() => handlePerformanceClick('cosco-performance')}
              >
                <td><strong>COSCO Shipping</strong></td>
                <td>32</td>
                <td>20.5h</td>
                <td><span className="status-indicator status-medium">90.2%</span></td>
                <td><span className="status-indicator status-medium">4.2/5</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Additional Insights */}
      <div className="insights-container" style={{marginTop: '30px'}}>
        <div 
          className="insight-card clickable"
          onClick={() => openDrillDown && openDrillDown('insight', {
            title: 'Market Concentration Analysis',
            icon: 'fas fa-chart-pie',
            iconColor: '#4da6ff',
            category: 'Market Structure',
            summary: 'Top 4 shipping lines control 84.7% of port traffic, indicating moderate market concentration.',
            details: 'Market concentration analysis reveals competitive dynamics and barriers to entry.',
            keyMetrics: [
              { label: 'Top 4 Market Share', value: '84.7%', change: '+1.2%' },
              { label: 'Herfindahl Index', value: '2,150', change: '+50 points' },
              { label: 'Market Concentration', value: 'Moderate', change: 'Stable' },
              { label: 'New Entrants', value: '2', change: 'New lines in 2023' }
            ],
            concentrationAnalysis: {
              'Definition': 'Moderate concentration (HHI 1,500-2,500)',
              'Competition Level': 'Moderate competition',
              'Barriers to Entry': 'High capital requirements',
              'Pricing Power': 'Moderate for major lines'
            },
            competitiveImplications: [
              'Price stability due to limited competition',
              'Service reliability from established players',
              'Limited choices for customers',
              'High barriers for new entrants'
            ],
            marketDynamics: [
              'Mergers and acquisitions continuing',
              'Technology becoming key differentiator',
              'Sustainability requirements increasing',
              'Customer demand for digital services'
            ],
            regulatoryConsiderations: [
              'Antitrust monitoring required',
              'Fair competition policies',
              'Port access regulations',
              'Environmental compliance'
            ],
            strategicOpportunities: [
              'Niche market development',
              'Technology partnerships',
              'Sustainability leadership',
              'Customer experience focus'
            ],
            riskFactors: [
              'Regulatory scrutiny increasing',
              'Customer pressure for alternatives',
              'Technology disruption potential',
              'Environmental regulation costs'
            ],
            policyRecommendations: [
              'Monitor market concentration',
              'Ensure fair port access',
              'Promote technology innovation',
              'Support sustainable practices'
            ],
            forecast: 'Expected to maintain current concentration levels through 2024',
            confidence: 'Medium-High (established market structure)'
          }, 'Market Analysis')}
        >
          <div className="insight-title"><i className="fas fa-chart-pie"></i> Market Concentration</div>
          <div className="insight-content">Top 4 shipping lines control 84.7% of port traffic, indicating moderate market concentration.</div>
        </div>
        
        <div 
          className="insight-card success clickable"
          onClick={() => openDrillDown && openDrillDown('insight', {
            title: 'Growth Rate Analysis',
            icon: 'fas fa-rocket',
            iconColor: '#4cd964',
            category: 'Performance Analysis',
            summary: 'COSCO Shipping shows strongest growth at +7.5% quarterly, gaining market share.',
            details: 'Growth analysis reveals competitive dynamics and emerging market leaders.',
            keyMetrics: [
              { label: 'COSCO Growth', value: '+7.5%', change: 'Quarterly' },
              { label: 'Market Share Gain', value: '+1.8%', change: '6 months' },
              { label: 'Volume Growth', value: '+28%', change: 'Annual' },
              { label: 'Rank Improvement', value: 'Rising', change: 'Competitive position' }
            ],
            growthDrivers: [
              'Expanded Asia-Europe service network',
              'Competitive pricing strategy',
              'Improved transit times',
              'Enhanced customer service quality'
            ],
            comparativeGrowth: [
              { line: 'COSCO', growth: '+7.5%', shareGain: '+0.4%' },
              { line: 'MSC', growth: '+5.1%', shareGain: '+0.5%' },
              { line: 'Maersk', growth: '+3.2%', shareGain: '+0.3%' },
              { line: 'CMA CGM', growth: '+2.8%', shareGain: '+0.2%' }
            ],
            strategicInitiatives: [
              'New vessel deployments on key routes',
              'Digital transformation investments',
              'Customer relationship enhancements',
              'Operational efficiency improvements'
            ],
            marketImpact: [
              'Increased competition on Asia-Europe routes',
              'Pressure on pricing from competitors',
              'Improved service options for customers',
              'Technology adoption acceleration'
            ],
            sustainabilityConsiderations: [
              'New vessels with better efficiency',
              'Carbon reduction initiatives',
              'Digital documentation adoption',
              'Slow steaming implementation'
            ],
            challenges: [
              'Maintaining growth momentum',
              'Managing expansion costs',
              'Integrating new services',
              'Competitive response from incumbents'
            ],
            opportunities: [
              'Further market share gains',
              'Technology leadership potential',
              'Sustainability differentiation',
              'Customer experience innovation'
            ],
            forecast: 'Expected to continue above-market growth through 2024',
            confidence: 'Medium (competitive market dynamics)'
          }, 'Growth Analysis')}
        >
          <div className="insight-title"><i className="fas fa-rocket"></i> Fastest Growing Entity</div>
          <div className="insight-content">COSCO Shipping shows strongest growth at +7.5% quarterly, gaining market share.</div>
        </div>
        
        <div 
          className="insight-card warning clickable"
          onClick={() => openDrillDown && openDrillDown('insight', {
            title: 'Service Quality Analysis',
            icon: 'fas fa-tachometer-alt',
            iconColor: '#ff9f40',
            category: 'Performance Metrics',
            summary: 'Turnaround time varies from 18.5 to 20.5 hours across shipping lines, indicating service level differences.',
            details: 'Service quality analysis reveals operational efficiency variations and customer impact.',
            keyMetrics: [
              { label: 'Best Turnaround', value: '18.5h (Maersk)', change: 'Industry leader' },
              { label: 'Worst Turnaround', value: '20.5h (COSCO)', change: 'Needs improvement' },
              { label: 'Industry Average', value: '19.8h', change: 'Benchmark' },
              { label: 'Service Gap', value: '2.0 hours', change: 'Significant difference' }
            ],
            serviceMetrics: [
              { metric: 'Turnaround Time', best: 'Maersk (18.5h)', worst: 'COSCO (20.5h)', gap: '2.0h' },
              { metric: 'On-time Performance', best: 'Maersk (96.2%)', worst: 'COSCO (90.2%)', gap: '6.0%' },
              { metric: 'Customer Rating', best: 'Maersk (4.8/5)', worst: 'COSCO (4.2/5)', gap: '0.6 points' },
              { metric: 'Vessels Deployed', best: 'Maersk (42)', worst: 'COSCO (32)', gap: '10 vessels' }
            ],
            efficiencyFactors: [
              'Operational process optimization',
              'Technology adoption level',
              'Workforce training and skills',
              'Port relationships and coordination'
            ],
            customerImpact: [
              'Shorter turnaround reduces costs',
              'Higher reliability improves planning',
              'Better service enhances satisfaction',
              'Competitive advantage for efficient lines'
            ],
            improvementOpportunities: [
              'Standardize operational procedures',
              'Share best practices across lines',
              'Invest in port infrastructure',
              'Enhance digital coordination'
            ],
            competitiveImplications: [
              'Service quality differentiates leaders',
              'Efficiency impacts profitability',
              'Customer loyalty tied to performance',
              'Market share influenced by service'
            ],
            benchmarkingRecommendations: [
              'Establish industry service standards',
              'Regular performance measurement',
              'Transparent reporting to customers',
              'Continuous improvement programs'
            ],
            strategicActions: [
              'Invest in port efficiency initiatives',
              'Adopt digital scheduling systems',
              'Train operational staff regularly',
              'Collaborate with port authorities'
            ],
            forecast: 'Expected service gap to narrow as competitors improve efficiency',
            confidence: 'Medium (industry focus on efficiency)'
          }, 'Service Analysis')}
        >
          <div className="insight-title"><i className="fas fa-tachometer-alt"></i> Service Level Variance</div>
          <div className="insight-content">Turnaround time varies from 18.5 to 20.5 hours across shipping lines, indicating service level differences.</div>
        </div>
      </div>
    </div>
  );
};

export default EntityTrafficTab;